import { apiRequest } from "@/helper/api";
import {
  AUDIT_REFERENCE_TARGETS,
  auditReferenceId,
  auditReferenceKey,
} from "@/components/auditLog/auditReference";
import type {
  AuditField,
  AuditReferenceLabels,
} from "@/components/auditLog/auditLogTypes";

const BATCH_SIZE = 40;
type ReferenceRecord = Record<string, unknown>;

export async function resolveAuditReferences(
  fields: AuditField[],
  signal: AbortSignal
): Promise<AuditReferenceLabels> {
  const requested = new Map<string, Set<string>>();
  for (const { reference } of fields) {
    if (!reference || !Object.hasOwn(AUDIT_REFERENCE_TARGETS, reference.model))
      continue;
    const ids = requested.get(reference.model) ?? new Set<string>();
    for (const value of [reference.before, reference.after]) {
      const id = auditReferenceId(value);
      if (id) ids.add(id);
    }
    requested.set(reference.model, ids);
  }

  const records = new Map<string, Map<string, ReferenceRecord>>();
  const fetchRecords = async (model: string, ids: Set<string>) => {
    const target = AUDIT_REFERENCE_TARGETS[model];
    const found = new Map<string, ReferenceRecord>();
    records.set(model, found);
    const uniqueIds = [...ids];
    for (let start = 0; start < uniqueIds.length; start += BATCH_SIZE) {
      const batch = uniqueIds.slice(start, start + BATCH_SIZE);
      let offset = 0;
      try {
        while (!signal.aborted && offset < batch.length) {
          const response = await apiRequest<
            | ReferenceRecord[]
            | { results: ReferenceRecord[]; next?: string | null }
          >({
            entity: target.entity,
            method: "get",
            parametersToRender: {
              fields: target.fields,
              id__in: batch.join(","),
              limit: batch.length,
              offset,
            },
            config: { signal },
            disableGlobalSpinner: true,
          });
          if (signal.aborted) return;
          const rows = Array.isArray(response.data)
            ? response.data
            : response.data.results;
          for (const row of rows) {
            const id = auditReferenceId(row.id);
            if (id && batch.includes(id)) found.set(id, row);
          }
          if (
            rows.length === 0 ||
            Array.isArray(response.data) ||
            !response.data.next
          )
            break;
          offset += rows.length;
        }
      } catch {
        break; // Keep unresolved IDs and avoid repeating requests to an unavailable or forbidden endpoint.
      }
      if (signal.aborted) return;
    }
  };

  const subjects = new Set(requested.get("appdata.subject"));
  const groups = [...requested].filter(
    ([model]) => model !== "appdata.subject"
  );
  for (let start = 0; start < groups.length && !signal.aborted; start += 4)
    await Promise.all(
      groups
        .slice(start, start + 4)
        .map(([model, ids]) => fetchRecords(model, ids))
    );
  for (const [model, rows] of records) {
    const subjectField = AUDIT_REFERENCE_TARGETS[model].subjectField;
    if (!subjectField) continue;
    for (const row of rows.values()) {
      const id = auditReferenceId(row[subjectField]);
      if (id) subjects.add(id);
    }
  }
  if (!signal.aborted) await fetchRecords("appdata.subject", subjects);
  if (signal.aborted) return {};

  const labels: AuditReferenceLabels = {};
  for (const [model, ids] of requested) {
    for (const id of ids) {
      const row = records.get(model)?.get(id);
      if (!row) continue;
      const subjectField = AUDIT_REFERENCE_TARGETS[model].subjectField;
      const subjectId =
        model === "appdata.subject"
          ? id
          : subjectField && auditReferenceId(row[subjectField]);
      const subject =
        subjectId && records.get("appdata.subject")?.get(subjectId);
      const name = subject ? subject.name : row.name;
      if (typeof name === "string" && name.trim())
        labels[auditReferenceKey(model, id)] = {
          id,
          label: name,
          ...(subject && subjectId ? { subjectId } : {}),
        };
    }
  }
  return labels;
}
